import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '@dfh-corp-sites/ui'
import { useLocation } from '@gatsbyjs/reach-router'
import { ChevronDown, ChevronUp } from 'lucide-react'
import React, { useMemo, useState } from 'react'

import { MainMenuFragmentFragment } from '../../../graphql/gatsby'
import useMediaQuery from '../../../hooks/useMediaQuery'
import useSiteSettings from '../../../hooks/useSiteSettings'
import { removeDispensaryPrefixAndSlug } from '../../../lib/dispensaries'
import { useShouldHideHeader } from '../../../lib/header/hooks/useShouldHideHeader'
import { useSanityRoleAndContextBasedContent } from '../../../lib/sanity'
import { normalizeSanityLink } from '../../../lib/sanity/link'
import { filterMenuEntriesToDisplay } from '../../../utils/sanity/documents/menuEntry'
import { cn } from '../../utils/cn'
import MenuEntryLink from './MenuEntryLink'

interface BrandMenuEntryProps {
  entry: NonNullable<NonNullable<MainMenuFragmentFragment['mainMenu']>[number]>
  isActive: boolean
}

const BrandMenuEntry: React.FC<BrandMenuEntryProps> = ({ entry, isActive }) => {
  return (
    <NavigationMenuItem>
      <NavigationMenuLink
        active={isActive}
        asChild
        className={cn(
          'w-fit block',
          'text-2xl md:text-xs leading-[1.6] md:leading-6',
          'decoration-[3px] md:decoration-2 underline-offset-4 [text-underline-position:under]',
          'hover:font-medium hover:underline focus:font-medium focus:underline',
          { 'font-medium underline': isActive },
        )}
      >
        <MenuEntryLink link={entry.link}>{entry.name}</MenuEntryLink>
      </NavigationMenuLink>
    </NavigationMenuItem>
  )
}

export const BrandMenu: React.FC = () => {
  const shouldHideHeader = useShouldHideHeader()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false)

  const isMobile = useMediaQuery('(max-width: 767px)')

  const { brandMenu } = useSiteSettings() || {}
  const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
  const location = useLocation()

  const menuEntries = useMemo(
    () => filterMenuEntriesToDisplay(shouldDisplayContent)(brandMenu),
    [brandMenu, shouldDisplayContent],
  )

  const activeEntry = useMemo(() => {
    if (!menuEntries?.length) return null

    const path = removeDispensaryPrefixAndSlug(location?.pathname ?? '/')
    return menuEntries.find((entry) => {
      const { link } = entry
      const normalizedHref = link ? normalizeSanityLink({ link })?.href : undefined

      return normalizedHref === path
    })
  }, [menuEntries, location.pathname])

  const menuEntryElements = useMemo(
    () =>
      menuEntries.map((entry) => (
        <BrandMenuEntry
          key={entry._id}
          entry={entry}
          isActive={!!entry._id && entry._id === activeEntry?._id}
        />
      )),
    [menuEntries, activeEntry],
  )

  return (
    <Collapsible open={!isMobile || isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen} asChild>
      <NavigationMenu
        className={cn(
          'relative max-w-auto transition-transform duration-300 flex-col md:flex-row xl:justify-start md:gap-10 px-6 md:py-2 bg-brand-navy text-brand-grey',
          shouldHideHeader ? '-translate-y-full' : 'translate-y-0',
        )}
        aria-label="Brand Menu"
      >
        <div className="flex gap-2.5 items-center">
          <span className="text-xs leading-5 md:leading-6">Designs for</span>
          <CollapsibleTrigger asChild>
            <Button
              variant="text"
              size="sm"
              className="md:hidden text-brand-grey text-xs leading-5 md:leading-6"
            >
              {activeEntry?.name ?? 'Health'}
              {isMobileMenuOpen ? (
                <ChevronUp className="size-5" />
              ) : (
                <ChevronDown className="size-5" />
              )}
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent asChild>
          <NavigationMenuList className="flex flex-col md:flex-row py-10 md:py-0 gap-4 md:gap-10 md:box-content space-x-0">
            {menuEntryElements}
          </NavigationMenuList>
        </CollapsibleContent>
      </NavigationMenu>
    </Collapsible>
  )
}
